<template>
  <Teleport to="body">
    <!-- Unsubscribe Confirmation Modal -->
    <div
      v-show="showUnsubscribeModal"
      class="modal-overlay"
      :class="{ show: unsubscribeModalVisible }"
      @click.self="hideUnsubscribeModal"
    >
      <div class="modal-content">
        <div class="modal-header">
          <h3 class="modal-title">{{ t("web.button.unsubscribe") }}</h3>
        </div>
        <div class="modal-body">
          <p>
            Are you sure you want to unsubscribe? You will no longer receive
            emails from this newsletter.
          </p>
          <template v-if="newsletter.unsubscription_reasons?.length">
            <p>
              {{ t("web.subscription.message.unsubscribe_reason_prompt") }}
            </p>
            <div class="unsubscription-reasons-list">
              <label
                v-for="reason in newsletter.unsubscription_reasons"
                :key="reason.id"
              >
                <input
                  v-model="selectedReason"
                  type="radio"
                  name="unsubscription-reason"
                  :value="reason.id"
                />
                <div>{{ reason.label }}</div>
              </label>
              <label>
                <input
                  v-model="selectedReason"
                  type="radio"
                  name="unsubscription-reason"
                  value="none-of-the-above"
                />
                {{ t("web.form.option.none_of_above") }}
              </label>
            </div>
          </template>
        </div>
        <div class="modal-footer">
          <button
            class="archive__button"
            type="button"
            @click="hideUnsubscribeModal"
          >
            {{ t("web.button.cancel") }}
          </button>
          <button
            class="archive__button"
            type="button"
            @click="confirmUnsubscribe"
          >
            {{ t("web.button.unsubscribe") }}
          </button>
        </div>
      </div>
    </div>

    <!-- Confirmation Modal for Unactivated Subscribers -->
    <div
      v-show="showConfirmationModal"
      class="modal-overlay"
      :class="{ show: confirmationModalVisible }"
    >
      <div class="modal-content">
        <div class="modal-header">
          <h3 class="modal-title">
            {{ t("web.subscription.message.almost_there") }}
          </h3>
        </div>
        <div class="modal-body">
          <p>
            {{ t("web.subscription.message.confirmation_email_sent") }}
            <strong>{{ subscriber?.email_address }}</strong
            >.
            {{ t("web.subscription.message.click_to_confirm") }}
          </p>
        </div>
        <div class="modal-footer">
          <button class="archive__button" type="submit" @click="handleRefresh">
            I've confirmed!
          </button>
        </div>
      </div>
    </div>
  </Teleport>

  <!-- Subscriber Comments Section -->
  <Teleport to="#subscriber-comments">
    <template v-if="comments.length > 0">
      <hr class="section-divider" />
      <div>
        <h2 class="section-title">Your comments</h2>
        <ol class="archive__comments-list">
          <li
            v-for="comment in comments"
            :key="comment.id"
            class="archive__comment-item"
          >
            <div class="comment">
              <div class="comment__content">
                {{ truncate(comment.text, 200) }}
              </div>
              <div class="comment__footer">
                <div class="comment__actions">
                  <a :href="comment.email?.absolute_url">
                    {{ comment.email?.subject }}
                  </a>
                  <span class="comment__timestamp">
                    {{ formatDate(comment.creation_date) }}
                  </span>
                  <a
                    href="#"
                    class="comment-delete-button"
                    @click.prevent="deleteComment(comment.id)"
                  >
                    {{
                      deletingCommentId === comment.id
                        ? "Deleting..."
                        : "Delete"
                    }}
                  </a>
                </div>
              </div>
            </div>
          </li>
        </ol>
      </div>
    </template>
  </Teleport>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from "vue";

import type { components as InternalOpenAPI } from "@/autogen/internal-openapi";
import type { components, paths } from "@/autogen/openapi";
import { createClient } from "@/lib/openapi";
import { extractEmailIdFromUrl } from "@/lib/subscribers/extractEmailIdFromUrl";
import type { Newsletter } from "@/types/newsletter";

import { createTranslator } from "./lib/utils";

const api = createClient<paths>({ base: "/v1" });

// The #subscriber blob is BasicSubscriberSchema
// (emails/views/subscriber_facing/schemas.py), exposed in the internal autogen.
type SubscriberData = InternalOpenAPI["schemas"]["BasicSubscriber"];
type CommentData = components["schemas"]["Comment"];

const props = defineProps<{
  newsletter: Newsletter;
}>();

const t = createTranslator(() => props.newsletter.locale);

// Reactive state
const subscriber = ref<SubscriberData | null>(null);
const showUnsubscribeModal = ref(false);
const unsubscribeModalVisible = ref(false);
const showConfirmationModal = ref(false);
const confirmationModalVisible = ref(false);
const selectedReason = ref("");
const isProcessing = ref(false);
const comments = ref<CommentData[]>([]);
const deletingCommentId = ref<string | null>(null);

function truncate(text: string, length: number): string {
  if (text.length <= length) return text;
  return text.slice(0, length) + "…";
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString(
    props.newsletter.locale || "en",
    {
      year: "numeric",
      month: "long",
      day: "numeric",
    }
  );
}

// Modal helpers
function openUnsubscribeModal() {
  showUnsubscribeModal.value = true;
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      unsubscribeModalVisible.value = true;
    });
  });
}

function hideUnsubscribeModal() {
  unsubscribeModalVisible.value = false;
  setTimeout(() => {
    showUnsubscribeModal.value = false;
  }, 200);
}

function openConfirmationModal() {
  showConfirmationModal.value = true;
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      confirmationModalVisible.value = true;
    });
  });
}

// API calls
async function processSubscriptionChange(
  subscriberType: components["schemas"]["SubscriberType"],
  button?: HTMLElement
) {
  if (!subscriber.value || isProcessing.value) return;

  isProcessing.value = true;
  if (button) {
    button.setAttribute("disabled", "true");
    button.innerText = "Processing...";
  }

  try {
    await api.patch("/subscribers/{id_or_email}", {
      params: { path: { id_or_email: subscriber.value.id } },
      body: { type: subscriberType },
    });
    window.location.reload();
  } catch (error) {
    console.error("Error:", error);
    alert("An error occurred. Please try again.");
    isProcessing.value = false;
    if (button) {
      button.removeAttribute("disabled");
      button.innerText = "Resubscribe";
    }
  }
}

// Unsubscribe runs through the unauthenticated /unsubscribe endpoint rather than
// the session-gated PATCH: a subscriber arriving cold via a footer link has no
// verified session (decision 0172 keeps unsubscribe one-click). Derive the
// endpoint from the management URL, which shares the same host and optional
// /{username} prefix.
async function submitUnsubscribe(button?: HTMLElement) {
  if (!subscriber.value || isProcessing.value) return;

  isProcessing.value = true;
  if (button) {
    button.setAttribute("disabled", "true");
    button.innerText = "Processing...";
  }

  const unsubscribeUrl = window.location.pathname.replace(
    /management\/subscribers\/[^/]+\/?$/,
    `unsubscribe/${subscriber.value.id}`
  );

  const body = new URLSearchParams();
  // Opt out of the canary bot-guard: this is an explicit, human-confirmed
  // unsubscribe, not link-prefetch or a List-Unsubscribe-Post.
  body.set("from_management_page", "1");
  if (selectedReason.value) {
    body.set("unsubscription_reason", selectedReason.value);
  }
  const email = window.location.search.includes("email=")
    ? window.location.search.split("email=")[1].split("&")[0]
    : null;
  const extractedEmailId = extractEmailIdFromUrl(email);
  if (extractedEmailId) {
    body.set("email", extractedEmailId);
  }

  try {
    const response = await fetch(unsubscribeUrl, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
    });
    if (!response.ok) throw new Error("Unsubscribe failed");
    window.location.reload();
  } catch (error) {
    console.error("Error:", error);
    alert("An error occurred. Please try again.");
    isProcessing.value = false;
    if (button) {
      button.removeAttribute("disabled");
      button.innerText = "Unsubscribe";
    }
  }
}

async function saveChanges(
  emailInput: HTMLInputElement | null,
  referralCodeInput: HTMLInputElement | null,
  referralLink: HTMLElement | null,
  saveChangesButton: HTMLElement
) {
  if (!subscriber.value) return;

  const email = emailInput?.value.trim() ?? null;
  const referralCode = referralCodeInput?.value.trim().toLowerCase() ?? null;

  if (email && !email.includes("@")) {
    alert("Please enter a valid email address");
    return;
  }

  if (
    referralCode &&
    (referralCode.length !== 6 || !/^[a-z]{6}$/.test(referralCode))
  ) {
    alert("Referral code must be exactly 6 letters (a-z)");
    return;
  }

  const body: paths["/subscribers/{id_or_email}"]["patch"]["requestBody"]["content"]["application/json"] =
    {};
  if (email) body.email_address = email;

  saveChangesButton.setAttribute("disabled", "true");
  saveChangesButton.innerText = "Saving...";

  try {
    const { error } = await api.patch("/subscribers/{id_or_email}", {
      params: { path: { id_or_email: subscriber.value.id } },
      body,
    });

    if (error) {
      throw new Error("An error occurred. Please try again.");
    }

    if (referralCode && referralLink) {
      referralLink.innerText =
        props.newsletter.absolute_url + "?utm_referral=" + referralCode;
    }

    window.location.reload();
  } catch (error) {
    console.error("Error:", error);
    alert("An error occurred. Please try again.");
    window.location.reload();
  }
}

async function updateMetadata(updateButton: HTMLElement) {
  if (!subscriber.value) return;

  const metadataFields = document.querySelectorAll(".metadata-field");
  const metadata: Record<string, string | boolean> = {};

  metadataFields.forEach((field) => {
    const el = field as HTMLInputElement | HTMLSelectElement;
    const key = el.dataset.fieldKey;
    const type = el.dataset.fieldType;
    if (!key) return;
    if (type === "checkbox") {
      metadata[key] = (el as HTMLInputElement).checked;
    } else {
      metadata[key] = el.value;
    }
  });

  const tagFields = document.querySelectorAll(".tag-field");
  const tags: string[] = [];
  tagFields.forEach((field) => {
    const el = field as HTMLInputElement;
    if (el.checked) tags.push(el.dataset.tagId!);
  });

  updateButton.setAttribute("disabled", "true");
  updateButton.innerText = "Updating...";

  const body: paths["/subscribers/{id_or_email}"]["patch"]["requestBody"]["content"]["application/json"] =
    {};
  if (Object.keys(metadata).length > 0) body.metadata = metadata;
  if (tagFields.length > 0) body.tags = tags;

  try {
    const { error } = await api.patch("/subscribers/{id_or_email}", {
      params: { path: { id_or_email: subscriber.value.id } },
      body,
    });

    if (!error) {
      window.location.reload();
    } else {
      alert("An error occurred. Please try again.");
      updateButton.removeAttribute("disabled");
      updateButton.innerText = "Update";
    }
  } catch (error) {
    console.error("Error:", error);
    alert("An error occurred. Please try again.");
    updateButton.removeAttribute("disabled");
    updateButton.innerText = "Update";
  }
}

async function fetchComments() {
  if (!props.newsletter.enabled_features?.includes("comments")) return;

  try {
    const response = await fetch(
      "/v1/comments?expand=email&ordering=-creation_date"
    );
    if (!response.ok) return;
    const data = await response.json();
    comments.value = data.results ?? [];
  } catch {
    // Silently fail — comments are supplementary
  }
}

async function deleteComment(commentId: string) {
  if (!confirm("Are you sure you want to delete this comment?")) return;

  deletingCommentId.value = commentId;
  try {
    const response = await fetch(`/v1/comments/${commentId}`, {
      method: "DELETE",
    });
    if (response.ok || response.status === 204) {
      comments.value = comments.value.filter((c) => c.id !== commentId);
    } else {
      alert("An error occurred. Please try again.");
    }
  } catch {
    alert("An error occurred. Please try again.");
  } finally {
    deletingCommentId.value = null;
  }
}

function handleRefresh() {
  window.location.reload();
}

function confirmUnsubscribe() {
  const subscriptionButton = document.getElementById("subscription-button");
  hideUnsubscribeModal();
  submitUnsubscribe(subscriptionButton ?? undefined);
}

// Keyboard handler
function handleKeydown(e: KeyboardEvent) {
  if (e.key === "Escape" && unsubscribeModalVisible.value) {
    hideUnsubscribeModal();
  }
}

onMounted(async () => {
  const subscriberElement = document.getElementById("subscriber");
  if (!subscriberElement) return;

  await import("@/subscriber_facing/styles/manage-subscription.css");

  subscriber.value = JSON.parse(subscriberElement.textContent || "{}");

  // DOM references for server-rendered form elements
  const subscriptionButton = document.getElementById("subscription-button");
  const updateButton = document.getElementById("update-button");
  const saveChangesButton = document.getElementById("save-changes-button");
  const emailInput = document.getElementById(
    "email-input"
  ) as HTMLInputElement | null;
  const referralCodeInput = document.getElementById(
    "referral-code-input"
  ) as HTMLInputElement | null;
  const referralLink = document.getElementById("referral-link");

  // Auto-open unsubscribe modal from URL hash
  if (window.location.hash === "#unsubscribe") {
    if (subscriber.value?.type !== "unsubscribed") {
      openUnsubscribeModal();
    } else {
      window.history.replaceState(null, "", window.location.pathname);
    }
  }

  // Show confirmation modal for unactivated subscribers
  if (subscriber.value?.type === "unactivated") {
    openConfirmationModal();
  }

  // Attach event listeners to server-rendered buttons
  subscriptionButton?.addEventListener("click", function () {
    const action = (this as HTMLElement).dataset.action;
    if (action === "unsubscribe") {
      openUnsubscribeModal();
      return;
    }
    processSubscriptionChange("regular", subscriptionButton);
  });

  saveChangesButton?.addEventListener("click", () => {
    saveChanges(emailInput, referralCodeInput, referralLink, saveChangesButton);
  });

  updateButton?.addEventListener("click", () => {
    updateMetadata(updateButton);
  });

  document.addEventListener("keydown", handleKeydown);

  // Fetch comments async
  fetchComments();
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleKeydown);
});
</script>
